/**
 * Front-my-End — Reactive State Store
 *
 * A production-grade, pub/sub-based state container that serves as the
 * single source of truth for cross-feature application state.
 *
 * Guardrails:
 * 1. Loop Protection (Shallow Equal): Returns immediately on set() if value is unchanged.
 * 2. BehaviorSubject Replay: Instantly invokes the callback with the current state upon subscription.
 */
class StateStore {
  constructor() {
    this.state = {
      html: '',
      css: '',
      js: '',
      activeTheme: 'light-mode',
      activeTab: 'html',
      liveEnabled: false,
      activePreviewRoute: null,
      activePreviewPath: 'index.html',
      forcedView: 'auto',
    };
    this.subscribers = {};
  }

  /**
   * Updates a state property and notifies subscribers if the value has changed.
   * @param {string} key - The state key to update
   * @param {any} value - The new value
   */
  set(key, value) {
    if (!(key in this.state)) {
      console.warn(`[Store Warning] Key "${key}" is not registered in the schema.`);
    }

    // 1. Loop Protection (Shallow Equal check)
    if (this.state[key] === value) return;

    this.state[key] = value;
    this.notify(key);
  }

  /**
   * Returns the current value of a state property.
   * @param {string} key - The state key to retrieve
   * @returns {any}
   */
  get(key) {
    return this.state[key];
  }

  /**
   * Subscribes to a state property. Immediately replays the current value.
   * @param {string} key - The state key to subscribe to
   * @param {function} callback - Callback function receiving the new value
   * @returns {function} Unsubscribe handle function
   */
  subscribe(key, callback) {
    if (!this.subscribers[key]) {
      this.subscribers[key] = [];
    }
    this.subscribers[key].push(callback);

    // 2. BehaviorSubject-style Replay: immediately fire with current value
    try {
      callback(this.state[key]);
    } catch (err) {
      console.error(`[Store Subscriber Error] Exception during initial replay for key "${key}":`, err);
    }

    // Return unsubscribe function
    return () => {
      this.subscribers[key] = this.subscribers[key].filter(cb => cb !== callback);
    };
  }

  /**
   * Notifies all subscribers of a key change.
   * @private
   */
  notify(key) {
    const list = this.subscribers[key];
    if (!list) return;

    const val = this.state[key];
    list.forEach(callback => {
      try {
        callback(val);
      } catch (err) {
        console.error(`[Store Subscriber Error] Exception in subscriber for key "${key}":`, err);
      }
    });
  }
}

// Single instance exported globally
export const store = new StateStore();
